/**
 * SKIRT OWNERSHIP — who owes a skirt, what the coarse side gives up for it, and how the fine side's
 * curves are assembled before skirtZip.ts is asked to bridge them. C3 of docs/display-contract.md.
 */

import { GRID_SIZE } from '@worldify/shared';
import { LAYER_COUNT } from './LayerConfig.js';

/** Face ids, matching SurfaceNet's boundaryFlags bits: [lowX, highX, lowY, highY, lowZ, highZ]. */
export const FACE_LOW_X = 0, FACE_HIGH_X = 1;
export const FACE_LOW_Y = 2, FACE_HIGH_Y = 3;
export const FACE_LOW_Z = 4, FACE_HIGH_Z = 5;
export const FACE_COUNT = 6;

/** A face's axis (0/1/2) and whether it is the HIGH (+) one. */
export const faceAxis = (face: number): number => face >> 1;
export const faceIsHigh = (face: number): boolean => (face & 1) === 1;

export const ALL_LAYERS: readonly number[] = Array.from({ length: LAYER_COUNT }, (_, i) => i);

// ---------------------------------------------------------------------------
// Reconciliation input

/**
 * The finer chunks across one face of an owner, WITH the lod each was generated at.
 *
 * Parallel arrays rather than a list of keys: the generator knows every candidate's lod — it is its
 * own loop variable — and every consumer needs it, so recovering it from the key meant a
 * `parseChunkKey` (a split, four substrings and an object) twice per candidate, ~120 per owner.
 */
export interface FinerCandidates {
  readonly keys: readonly string[];
  /** Parallel to `keys`. */
  readonly lods: readonly number[];
}

/** One skirt record: a COARSE owner, a LAYER, and the faces of that owner across which a finer chunk */
export interface RequiredSkirt {
  /** The coarse chunk that owns (and will carry) this skirt. */
  owner: string;
  layer: number;
  /** Owner faces that are cross-LOD-finer, ascending. */
  faces: number[];
  /** The displayed finer neighbours across each of `faces`, parallel to it. */
  finerNeighbours: string[][];
  /** Of `faces`, the + ones — the only faces where the OWNER is the low side of the plane. */
  maskFaces: number[];
}

export class SkirtManager {
  /** Stable identity of a skirt record, for 5b's add/keep/drop diff. */
  static skirtId(owner: string, layer: number): string {
    return `${owner}|${layer}`;
  }

  /**
   * THE SET OF SKIRTS A DISPLAYED CONFIGURATION REQUIRES — pure, so the reconciliation that 5b runs
   * every commit is a diff of two sets rather than a walk of the scene graph.
   */
  static computeRequiredSkirts(
    displayedKeys: Iterable<string>,
    isDisplayed: (key: string) => boolean,
    lodOfKey: (key: string) => number,
    finerNeighboursOf: (key: string, face: number) => FinerCandidates,
    layers: readonly number[] = ALL_LAYERS,
  ): RequiredSkirt[] {
    const out: RequiredSkirt[] = [];
    for (const owner of displayedKeys) {
      for (const r of SkirtManager.computeOwnerSkirts(
        owner, isDisplayed, lodOfKey, finerNeighboursOf, layers,
      )) out.push(r);
    }
    return out;
  }

  /**
   * ONE OWNER'S RECORDS — the per-owner unit the whole-set computation above is a loop over.
   *
   * Split out because the answer for one owner reads only its OWN display state and lod plus those
   * of its finer-neighbour candidates: nothing global. That is what lets a caller memoize by owner
   * and recompute only the owners a change can actually reach, instead of re-enumerating every
   * candidate whenever any one chunk moves.
   */
  static computeOwnerSkirts(
    owner: string,
    isDisplayed: (key: string) => boolean,
    lodOfKey: (key: string) => number,
    finerNeighboursOf: (key: string, face: number) => FinerCandidates,
    layers: readonly number[] = ALL_LAYERS,
  ): RequiredSkirt[] {
    const out: RequiredSkirt[] = [];
    if (!isDisplayed(owner)) return out;
    const lod = lodOfKey(owner);
    if (lod <= 0) return out;                       // nothing can be finer than the finest level
    const faces: number[] = [];
    const finerNeighbours: string[][] = [];
    const maskFaces: number[] = [];
    for (let face = 0; face < FACE_COUNT; face++) {
      const cand = finerNeighboursOf(owner, face);
      const seen = new Set<string>();
      const finer: string[] = [];
      for (let i = 0; i < cand.keys.length; i++) {
        const n = cand.keys[i];
        // Cheapest test first, and `isDisplayed` last — it is the one that reaches the scene graph.
        if (cand.lods[i] >= lod || seen.has(n) || !isDisplayed(n)) continue;
        seen.add(n);
        finer.push(n);
      }
      if (finer.length === 0) continue;
      faces.push(face);
      finerNeighbours.push(finer);
      if (faceIsHigh(face)) maskFaces.push(face);
    }
    if (faces.length === 0) return out;
    for (const layer of layers) {
      out.push({
        owner, layer, faces: [...faces], maskFaces: [...maskFaces],
        finerNeighbours: finerNeighbours.map((f) => [...f]),
      });
    }
    return out;
  }

  // -------------------------------------------------------------------------
  // The coarse owner's ring

  /** THE TRIANGLES A COARSE OWNER MUST COLLAPSE so it never draws past the shared plane (C1). */
  static ringMaskTargets(
    cellIndices: ArrayLike<number>,
    indices: ArrayLike<number>,
    faces: Iterable<number>,
    opts: { maskCells?: number; grid?: number } = {},
  ): Uint32Array {
    const grid = opts.grid ?? GRID_SIZE;
    const cells = opts.maskCells ?? 1;
    const axes: number[] = [];
    for (const face of faces) {
      if (!faceIsHigh(face)) continue;
      const a = faceAxis(face);
      if (!axes.includes(a)) axes.push(a);
    }
    if (axes.length === 0) return new Uint32Array(0);
    const divs = axes.map((a) => (a === 0 ? 1 : a === 1 ? grid : grid * grid));
    const inBand = (v: number): boolean => {
      for (const div of divs) {
        if (Math.floor(cellIndices[v] / div) % grid > grid - 2 - cells) return true;
      }
      return false;
    };
    const out: number[] = [];
    for (let t = 0; t * 3 + 2 < indices.length; t++) {
      const a = t * 3;
      if (indices[a] === indices[a + 1]) continue;             // already collapsed
      if (!inBand(indices[a]) && !inBand(indices[a + 1]) && !inBand(indices[a + 2])) continue;
      out.push(t);
    }
    return Uint32Array.from(out);
  }

  


}
